import http from '@/lib/request/svc/hr/http';
import getInstallationId from '@/lib/facades/get-installation-id';
import jobTitleErrorReason from '@/lib/request/svc/hr/job-title-error-reason';

/** The endpoint rejects the whole envelope past this many employees. */
export const BULK_SET_BUSINESS_ENTITY_EMPLOYEE_LIMIT = 100;

export interface BulkSetBusinessEntityRowError {
  employeeId: string;
  reason: string;
}

export interface BulkSetBusinessEntityResult {
  updated: string[];
  created: string[];
  errors: BulkSetBusinessEntityRowError[];
}

export interface BulkSetBusinessEntityParams {
  businessEntityId: string;
  employeeIds: string[];
  /**
   * y-m-d on the employees' location clock (`locationToday`). Read only for
   * employees given a new assignment.
   */
  effectiveFrom: string;
}

interface RawAssignment {
  employeeId?: string;
}

interface RawRowError {
  employeeId?: string;
  reason?: string;
}

interface RawBulkSetBusinessEntityEnvelope {
  success?: boolean;
  data?: {
    updated?: RawAssignment[];
    created?: RawAssignment[];
    errors?: RawRowError[];
  };
  error?: { message?: string };
}

function toEmployeeIds(entries: RawAssignment[] | undefined): string[] {
  return (Array.isArray(entries) ? entries : [])
    .map(entry => entry?.employeeId)
    .filter((id): id is string => typeof id === 'string');
}

function refusalMessage(
  envelope: RawBulkSetBusinessEntityEnvelope | undefined
): string | undefined {
  return envelope?.error?.message || jobTitleErrorReason(envelope);
}

/**
 * proto3-JSON omits empty arrays, so each list is read by shape. `success`
 * false carrying row errors is the every-row-failed result and is returned
 * like any other. A refusal throws with svc-hr's message, whether it came back
 * 200 with no row errors or as an error status.
 */
export async function bulkSetBusinessEntity({
  businessEntityId,
  employeeIds,
  effectiveFrom,
}: BulkSetBusinessEntityParams): Promise<BulkSetBusinessEntityResult> {
  if (employeeIds.length > BULK_SET_BUSINESS_ENTITY_EMPLOYEE_LIMIT) {
    throw new Error(
      `bulkSetBusinessEntity accepts at most ${BULK_SET_BUSINESS_ENTITY_EMPLOYEE_LIMIT} employees`
    );
  }

  const tenantId = getInstallationId();
  let response: unknown;

  try {
    ({ data: response } = await http.post(
      `/tenants/${tenantId}/job-assignments:bulk-set-business-entity`,
      { data: { businessEntityId, employeeIds, effectiveFrom } }
    ));
  } catch (e) {
    const body = (e as { response?: { data?: unknown } } | undefined)?.response
      ?.data as RawBulkSetBusinessEntityEnvelope | undefined;
    const message = body && refusalMessage(body);

    if (!message) {
      throw e;
    }

    throw new Error(message);
  }

  const envelope = response as RawBulkSetBusinessEntityEnvelope | undefined;
  const errors = (
    Array.isArray(envelope?.data?.errors) ? envelope.data.errors : []
  )
    .filter(
      (entry): entry is RawRowError & { employeeId: string } =>
        typeof entry?.employeeId === 'string'
    )
    .map(entry => ({
      employeeId: entry.employeeId,
      reason: typeof entry.reason === 'string' ? entry.reason : '',
    }));

  if (envelope?.success !== true && !errors.length) {
    throw new Error(
      refusalMessage(envelope) || 'bulk set business entity failed'
    );
  }

  return {
    updated: toEmployeeIds(envelope?.data?.updated),
    created: toEmployeeIds(envelope?.data?.created),
    errors,
  };
}

export default bulkSetBusinessEntity;
